import api from './api'
import type { User } from '@/models/User'

export interface UpdateUserProfileData {
    name?: string
}

export interface UpdateUserImageData {
    profileImage: File
}

export interface UpdatePasswordData {
    current_password: string
    password: string
    password_confirmation: string
}

export interface AdminStats {
    devices: number
    users: number
    messages: number
}

interface PaginationParams {
    page?: number
    per_page?: number
    search?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
}

interface PaginatedResponse<T> {
    data: T[]
    pagination: {
        current_page: number
        per_page: number
        total: number
        total_pages: number
        has_next: boolean
        has_prev: boolean
    }
}

class UserService {
    async getUsers(params: PaginationParams = {}): Promise<PaginatedResponse<User>> {
        try {
            const defaultParams = {
                page: params.page || 1,
                per_page: params.per_page || 10,
                ...(params.search && { search: params.search }),
                ...(params.sort_by && { sort_by: params.sort_by }),
                ...(params.sort_order && { sort_order: params.sort_order })
            }

            const response = await api.get('/users.json', {
                params: defaultParams
            })

            if (response.status === 200) {
                // Si l'API retourne déjà un format paginé
                if (response.data.data && response.data.pagination) {
                    return {
                        data: response.data.data,
                        pagination: response.data.pagination
                    }
                }

                // Sinon, on simule la pagination côté client
                const users = response.data
                const total = users.length
                const perPage = params.per_page || 10
                const currentPage = params.page || 1
                const totalPages = Math.ceil(total / perPage)
                const startIndex = (currentPage - 1) * perPage
                const endIndex = startIndex + perPage

                // Filtrage par recherche si nécessaire
                let filteredUsers = users
                if (params.search) {
                    const searchTerm = params.search.toLowerCase()
                    filteredUsers = users.filter((user: any) =>
                        (user.name && user.name.toLowerCase().includes(searchTerm)) ||
                        (user.email && user.email.toLowerCase().includes(searchTerm))
                    )
                }

                // Tri si nécessaire
                if (params.sort_by) {
                    filteredUsers.sort((a: any, b: any) => {
                        let aVal, bVal
                        switch (params.sort_by) {
                            case 'name':
                                aVal = a.name || ''
                                bVal = b.name || ''
                                break
                            case 'email':
                                aVal = a.email || ''
                                bVal = b.email || ''
                                break
                            case 'created_at':
                                aVal = a.created_at ? new Date(a.created_at).getTime() : 0
                                bVal = b.created_at ? new Date(b.created_at).getTime() : 0
                                break
                            case 'last_active_at':
                                aVal = a.last_active_at ? new Date(a.last_active_at).getTime() : 0
                                bVal = b.last_active_at ? new Date(b.last_active_at).getTime() : 0
                                break
                            case 'is_admin':
                                aVal = a.is_admin ? 1 : 0
                                bVal = b.is_admin ? 1 : 0
                                break
                            default:
                                aVal = a.name || ''
                                bVal = b.name || ''
                        }

                        if (params.sort_order === 'desc') {
                            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0
                        }
                        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
                    })
                }

                const paginatedData = filteredUsers.slice(startIndex, endIndex)

                return {
                    data: paginatedData,
                    pagination: {
                        current_page: currentPage,
                        per_page: perPage,
                        total: filteredUsers.length,
                        total_pages: Math.ceil(filteredUsers.length / perPage),
                        has_next: currentPage < Math.ceil(filteredUsers.length / perPage),
                        has_prev: currentPage > 1
                    }
                }
            }

            return {
                data: [],
                pagination: {
                    current_page: 1,
                    per_page: 10,
                    total: 0,
                    total_pages: 0,
                    has_next: false,
                    has_prev: false
                }
            }
        } catch (error) {
            console.error('Error fetching users:', error)
            return {
                data: [],
                pagination: {
                    current_page: 1,
                    per_page: 10,
                    total: 0,
                    total_pages: 0,
                    has_next: false,
                    has_prev: false
                }
            }
        }
    }

    async updateProfile(user: User, data: UpdateUserProfileData): Promise<User | null> {
        try {
            const formData = new FormData()

            if (data.name) {
                formData.append('user[name]', data.name)
            }

            const response = await api.put(`/users/${user.id}.json`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })

            if (response.status === 200) {
                const userData = response.data
                return {
                    id: userData.id,
                    email: userData.email,
                    name: userData.name,
                    authentication_token: userData.authentication_token,
                    profileImage: this.getProfileImageUrl(userData)
                }
            }
            return null
        } catch (error) {
            console.error('Error updating profile:', error)
            throw error
        }
    }

    async updatePassword(data: UpdatePasswordData): Promise<boolean> {
        try {
            const response = await api.put('/users', {
                user: data
            })

            return response.status === 200
        } catch (error) {
            console.error('Error updating password:', error)
            throw error
        }
    }

    async updateProfileImage(user: User, data: UpdateUserImageData): Promise<User | null> {
        try {
            const formData = new FormData()
            formData.append('image', data.profileImage)
            const response = await api.post(`/users/${user.id}/profile`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            console.log("Response:", response)
            if (response.status === 200) {
                return response.data
            }
            return null
        } catch (error) {
            console.error('Error updating profile image:', error)
            throw error
        }
    }

    async getStats(): Promise<AdminStats | null> {
        try {
            const response = await api.get('/users/stats.json')
            if (response.status === 200) {
                return response.data
            }
            return null
        } catch (error) {
            console.error('Error fetching admin stats:', error)
            throw error
        }
    }

    getProfileImageUrl(user: User): string {
        return `${api.defaults.baseURL}/data/users/${user.id}/mini.jpg?ver=${user.pic_ver}`
    }
}

export const userService = new UserService()
export default userService 